import { JobsApiRequest } from '@/api'
import InspectionsApiRequest from '@/api/inspections'
import JobTasksApiRequest from '@/api/job-tasks'
import { JobExpandablePropertyType, JobsFilterType, JobType } from '@/enum'
import {
  CustomFilterTypeExpression,
  DeleteTypeExpression,
  ItemTypeExpression,
  ListFilterTypeExpression,
  ListResponseTypeExpression
} from '@/enum/LinqExpressionType'
import StatusCodeType from '@/enum/StatusCodeType'
import JobRequestMapper from '@/mappers/JobRequestMapper'
import { CalendarEvent, InspectionForm, Job } from '@/models'
import ErrorHandlerService from '@/services/ErrorHandlerService'
import { defineStore } from 'pinia'
import usePaginationStore from '../pagination'
import state from './state'
import type { IExplicitlyUpdatedJobProperty } from '@/interfaces'
import type { AxiosResponse } from 'axios'
import { fetchUntilExhaustion, generateFilterExpressionForCalendarEvents } from '@/utils/helpers'
import useGlobalStore from '../global'
import {
  JOB_DETAILS_EXPAND_PARAMS,
  MESSAGE_CONSTANTS,
  UNSCHEDULED_JOBS_LIST_EXPAND_PARAMS
} from '@/utils/constants'
import ErrorType from '@/enum/ErrorType'
import NotificationType from '@/enum/NotificationType'

const useInspectionsStore = defineStore('inspections', {
  state: () => state,
  actions: {
    async getInspections(storeName: string, responseOptions?: any, isMinimal = false) {
      const inspectionsList = usePaginationStore(storeName)
      const inspectionsApi = new InspectionsApiRequest()
      if (!isMinimal) {
        responseOptions = {
          ...responseOptions,
          [ListResponseTypeExpression.Expand]: `
          ${responseOptions?.[ListResponseTypeExpression.Expand] ? `${responseOptions?.[ListResponseTypeExpression.Expand]},` : ''}
          ${JobExpandablePropertyType.ASSIGNED_EMPLOYEES},
          ${JobExpandablePropertyType.ASSIGNED_EMPLOYEES_EMPLOYEE},
          ${JobExpandablePropertyType.ASSET},
          ${JobExpandablePropertyType.ASSET_VEHICLE},
          ${JobExpandablePropertyType.ASSET_EQUIPMENT},
          ${JobExpandablePropertyType.ASSET_MACHINE},
          ${JobExpandablePropertyType.TASK_TEMPLATES},
          ${JobExpandablePropertyType.NEXT_TRIGGER_INFO}
          `
        }
      }
      inspectionsList.handleListAPICall(
        inspectionsApi.getInspections.bind(inspectionsApi),
        Job,
        responseOptions
      )
    },
    async getInspectionsForCalendar(start: string, end: string, options?: any) {
      const inspectionsApi = new InspectionsApiRequest()
      const responseOptions = generateFilterExpressionForCalendarEvents(
        JobType.INSPECTION,
        start,
        end,
        options
      )
      const result = await fetchUntilExhaustion(
        inspectionsApi.getInspectionsTasks.bind(inspectionsApi),
        responseOptions
      )
      return result.filter(
        (job) => job.scheduledAt || job.dueAt || job.startedAt || job.completedAt
      )
    },
    async createInspection(): Promise<AxiosResponse> {
      this.isInspectionFormLoading = true
      const jobsApi = new JobsApiRequest()
      const jobMapper = new JobRequestMapper()
      const globalStore = useGlobalStore()
      const newJob = jobMapper.toJobUpdate({ ...this.inspectionForm, type: JobType.INSPECTION })
      const result = await jobsApi.createJob(newJob?.jobRequestData)

      try {
        if (result.status === StatusCodeType.Success && result.data) {
          // show notification
          globalStore.addToastNotification({
            id: Symbol('Job added'),
            message: `${result.data?.title} added successfully`,
            type: NotificationType.Success
          })
        }
      } catch (error: any) {
        ErrorHandlerService.handleError(error, error)
      } finally {
        this.isInspectionFormLoading = false
      }
      return result
    },
    async getInspection(id: string) {
      const globalStore = useGlobalStore()
      try {
        this.isInspectionFormLoading = true
        const inspectionsApi = new InspectionsApiRequest(false, true)

        const result = await inspectionsApi.getInspection({
          id,
          [ItemTypeExpression.Expand]: JOB_DETAILS_EXPAND_PARAMS
        })
        if (result.status === StatusCodeType.Success && result.data) {
          this.inspectionForm = new InspectionForm(result.data)

          if (!this.inspectionForm.isRecurring()) {
            const jobTasksApi = new JobTasksApiRequest()
            const tasksResponse = await jobTasksApi.getJobTasks({ jobId: result.data.id })
            if (
              tasksResponse.status === StatusCodeType.Success &&
              tasksResponse.data?.value?.length
            ) {
              this.oneTimeInspectionTask = tasksResponse.data.value[0]
              this.inspectionForm.dueDate = this.oneTimeInspectionTask?.dueAt
            }
          }

          return result
        }
      } catch (error: any) {
        const title = error?.response.data.detail
        globalStore.addError(
          {
            title
          },
          ErrorType.Network
        )
        return error
      } finally {
        this.isInspectionFormLoading = false
      }
    },
    async updateInspection(
      inspectionId: string,
      explicitlyUpdatedInspectionProperty?: IExplicitlyUpdatedJobProperty
    ) {
      const jobsApi = new JobsApiRequest(false, true)
      const jobMapper = new JobRequestMapper()
      const updatedInspection = explicitlyUpdatedInspectionProperty ?? this.inspectionForm
      const newJob = jobMapper.toJobUpdate({ ...updatedInspection, type: JobType.INSPECTION })

      try {
        const result = await jobsApi.updateJob(
          {
            id: inspectionId,
            [ItemTypeExpression.Expand]: JOB_DETAILS_EXPAND_PARAMS
          },
          newJob.jobRequestData
        )

        if (result.status === StatusCodeType.Success) {
          const isOneTimeInspection = !this.inspectionForm.isRecurring()
          const hasDueDateChanged =
            this.inspectionForm.dueDate !== this.oneTimeInspectionTask?.dueAt

          if (isOneTimeInspection && hasDueDateChanged && this.oneTimeInspectionTask?.id) {
            const jobTasksApi = new JobTasksApiRequest()
            await jobTasksApi.updateJobTask(
              { jobId: inspectionId, taskId: this.oneTimeInspectionTask.id },
              {
                dueAt: this.inspectionForm.dueDate,
                status: this.oneTimeInspectionTask.status,
                updatedAt: this.oneTimeInspectionTask.updatedAt
              }
            )
          }
        }

        return result
      } catch (error: any) {
        const title =
          error?.response.data?.detail ?? MESSAGE_CONSTANTS.INSPECTION_WAS_DELETED_ALERT_TITLE

        const globalStore = useGlobalStore()
        if ([StatusCodeType.NotFound, StatusCodeType.Invalid].includes(error.status)) {
          globalStore.addError(
            {
              title
            },
            ErrorType.Network
          )
        }
        return error
      }
    },
    async deleteInspections(storeName: string) {
      const inspectionsList = usePaginationStore(storeName)
      try {
        const inspectionsApi = new InspectionsApiRequest()

        if (!inspectionsList.selectedItems) throw new Error('No jobs selected for deletion')
        const result = await inspectionsApi.deleteInspections({
          [DeleteTypeExpression.Expression]: { Id: inspectionsList.selectedItems }
        })
        return result
      } catch (error: any) {
        ErrorHandlerService.handleError(error, error)
      }
    },
    resetInspectionForm() {
      this.inspectionForm = new InspectionForm()
      this.oneTimeInspectionTask = null
    },

    async getInspectionsUnscheduledJobs(storeName: string) {
      const unscheduleJobsListStore = usePaginationStore(storeName)
      const jobsApi = new JobsApiRequest()

      const responseOptions = {
        [ListResponseTypeExpression.Expand]: UNSCHEDULED_JOBS_LIST_EXPAND_PARAMS,
        [ListFilterTypeExpression.Filter]: {
          [CustomFilterTypeExpression.Expression]: `(${JobsFilterType.NEXT_TRIGGER_INFO_DUE_AT}==null && ${JobsFilterType.START_DATE}==null)`,
          [JobsFilterType.JOB_TYPE]: JobType.INSPECTION
        }
      }

      unscheduleJobsListStore.loading = true

      try {
        await unscheduleJobsListStore.handleListAPICall<CalendarEvent>(
          jobsApi.getJobsTasks.bind(jobsApi),
          CalendarEvent,
          responseOptions
        )
        return true
      } catch (error) {
        return false
      } finally {
        unscheduleJobsListStore.loading = false
      }
    }
  }
})

export default useInspectionsStore
